<template>
  <div v-if="images?.length > 0" class="w-full max-w-7xl mx-auto flex h-full rounded-md select-none" :class="isVerticalLayout ? 'flex-row gap-4 items-start' : 'flex-col items-center'">
    <div class="flex flex-col w-full relative h-full overflow-hidden min-h-44 max-h-[calc(100vh-220px)]" :class="{ 'flex-1': isVerticalLayout }">
      <div ref="imgContent" class="relative w-full h-full overflow-hidden min-h-44 max-h-[calc(100vh-220px)]" @touchstart.prevent @mousedown.prevent>
      <UiProgress
        :percent="(model / (images.length - 1)) * 100"
        :no-text="true"
        height="2"
        type="primary"
        :tooltip="$t ? $t('carousel.progress') : ''"
      />

      <transition name="fade" mode="out-in">
        <img
          v-if="images.length"
          :key="images[model]"
          :src="images[model]"
          alt="Slide"
          class="object-contain w-full h-full select-none max-h-[43.5rem]"
          :class="{ 'opacity-0': expand }"
          @click="expand = true"
        />
      </transition>

      <!-- arrows -->
      <UiButton
        v-if="!isFirst"
        icon="chevron-left"
        aria-label="Anterior"
        class="p-2 absolute left-0 top-1/2 transform -translate-y-1/2"
        @click="prev"
      />

      <UiButton
        v-if="!isLast"
        icon="chevron-right"
        aria-label="Próximo"
        class="p-2 absolute right-0 top-1/2 transform -translate-y-1/2"
        @click="next"
      />
    </div>

    <div v-if="images.length" class="w-full flex items-center justify-center gap-2 mt-2">
      <span class="text-sm text-gray-500 dark:text-gray-400">
        {{ model + 1 }} / {{ images.length }}
      </span>
    </div>
    </div>

    <div v-if="showThumbnails" :style="isVerticalLayout ? { height: dynamicMaxHeight, maxHeight: dynamicMaxHeight } : {}" :class="isVerticalLayout ? 'overflow-y-hidden overflow-x-auto w-auto shrink-0 mt-0' : 'flex w-full mt-2 items-center justify-center'">
      <div ref="thumbContent" class="flex gap-2" :class="isVerticalLayout ? 'flex-col flex-wrap h-full content-start items-start justify-start' : 'flex-wrap items-center justify-center py-2'">
        <img
          v-for="(image, index) in images"
          :key="index"
          :src="image"
          alt="Thumbnail"
          class="w-12 h-12 lg:w-16 lg:h-16 object-cover rounded cursor-pointer transition-all duration-200 border-2 shrink-0"
          :class="{ 'opacity-100 border-primary-500': model === index, 'opacity-60 border-transparent hover:opacity-80': model !== index }"
          @click="model = index"
        />
      </div>
    </div>

    <VueEasyLightbox
      :visible="expand"
      :imgs="images"
      :index="model"
      @hide="() => expand = false"
      />
      <!-- @onIndexChange="(_, newIdx) => model = newIdx" -->
  </div>
  <div v-else class="w-full flex items-center justify-center py-4">
    Nenhuma imagem encontrada para este carrossel.
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { usePointerSwipe, onKeyStroke, useResizeObserver, useBreakpoints, breakpointsTailwind } from '@vueuse/core'

import UiButton from '../button.vue'
import UiProgress from '../progress.vue'

defineOptions({
  name: 'UiImageCarousel'
})

interface Props {
  images: string[]
  showThumbnails?: boolean
  thumbnailPosition?: 'horizontal' | 'vertical'
}

const { images = [], showThumbnails = true, thumbnailPosition = 'horizontal' } = defineProps<Props>()
const model = defineModel({ default: 0 })
const imgContent = ref<HTMLElement | null>(null)
const thumbContent = ref<HTMLElement | null>(null)
const expand = ref(false)
const dynamicMaxHeight = ref('calc(100vh - 220px)')

const breakpoints = useBreakpoints(breakpointsTailwind)
const isDesktop = breakpoints.greaterOrEqual('lg')
const isVerticalLayout = computed(() => thumbnailPosition === 'vertical' && isDesktop.value)

useResizeObserver(imgContent, (entries) => {
  const entry = entries[0]
  if (entry && isVerticalLayout.value) {
    dynamicMaxHeight.value = `${entry.contentRect.height}px`
  } else {
    dynamicMaxHeight.value = 'calc(100vh - 220px)'
  }
})

const emit = defineEmits(['update:modelValue', 'complete', 'onUpdateProgress'])

const { isSwiping, direction } = usePointerSwipe(imgContent)

const lastProgress = ref(0)

watch(model, (val) => {
  const progress = Math.round((val / (images.length - 1)) * 100)

  if (progress > lastProgress.value) {
    emit('onUpdateProgress', progress)
    lastProgress.value = progress
  }
  
  // Scroll thumbnail into view
  if (thumbContent.value) {
    const thumbImgs = thumbContent.value.querySelectorAll('img')
    const current = thumbImgs[val] as HTMLElement
    if (current) {
      current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'center' })
    }
  }
})

const isFirst = computed(() => model.value === 0)
const isLast = computed(() => model.value === images.length - 1)

const next = () => {
  if (!isLast.value) model.value++
}

const prev = () => {
  if (!isFirst.value) model.value--
}

onKeyStroke('ArrowRight', next)
onKeyStroke('ArrowLeft', prev)

watch(isSwiping, (nVal, oVal) => {
  if (oVal && !nVal) {
    if (direction.value === 'left') {
      next()
    } else if (direction.value === 'right') {
      prev()
    }
  }
})
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.4s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
